import { useMemo } from 'react';

import { MenuItem } from '@class-ui/react-components';
import CloseIcon from '@mui/icons-material/Close';
import { Button, Checkbox, Divider, FormControl, FormControlLabel, IconButton } from '@mui/material';
import { styled } from '@mui/material/styles';
import moment from 'moment';
import { Control, Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { AttendanceType } from './types';
import { ClassStudentGroup } from '../../context/types';
import { LmsSyncSettings } from '../../types/apiTypes';
import timezones from '../../utils/node12_timezones';
import ControlledAutocomplete from '../forms/ControlledAutocomplete';
import ControlledDateRange from '../forms/ControlledDateRange';
import ControlledInput from '../forms/ControlledInput';
import ControlledSelect from '../forms/ControlledSelect';
import { Error } from '../forms/styles';

const StyledCloseButton = styled(IconButton)`
    /* to align with icon inside of button */
    margin-left: auto;
`;

const FormContainer = styled('div')`
    display: flex;
    flex-direction: column;
`;

const ChildrenContainer = styled('div')`
    display: grid;
    grid-template-columns: 1fr auto 1fr auto 1.5fr;
    grid-template-rows: auto auto;
    grid-row-gap: 1rem;
`;

const Separator = styled('div')`
    margin: auto 1rem;
    padding-top: 1.5rem;
`;

const AcknowledgeOvernightContainer = styled('div')`
    grid-column: 1 / 6;
`;

const Spacer = styled('div')`
    margin: 0 1rem;
`;

const DateRangeContainer = styled('div')`
    width: 100%;
    grid-column: 1 / 4;

    & > div {
        display: flex;
        align-items: center;
    }
`;

const StyledAutocomplete = styled(FormControl)``;

const AddSessionsContainer = ({ children, onClose }) => (
    <FormContainer>
        {onClose && (
            <StyledCloseButton id="close-button" onClick={onClose}>
                <CloseIcon />
            </StyledCloseButton>
        )}
        <ChildrenContainer>{children}</ChildrenContainer>
    </FormContainer>
);

export interface AddSessionsFieldsProps {
    control: Control<any, any>;
    namePrefix?: string;
    onAddMore?: () => void;
    onClose?: () => void;
    isOvernight?: boolean;
    classGroups?: ClassStudentGroup[] | undefined;
    classSessionGroupUuid?: string;
    label?: string;
    selectedGroups?: string[];
    attendanceType?: AttendanceType;
    lmsSyncSettings?: LmsSyncSettings;
}

export const AddSessionsFields = (props: AddSessionsFieldsProps) => {
    const {
        control,
        namePrefix = '',
        onAddMore,
        onClose,
        isOvernight,
        classGroups,
        lmsSyncSettings,
        attendanceType
    } = props;
    const { t } = useTranslation('calendar');
    const timezoneOptions = useMemo(() => timezones.map((i) => ({ label: t(i.key), value: i.value })), [t]);

    const renderOvernightAcknowledgement = () => {
        if (isOvernight) {
            return (
                <AcknowledgeOvernightContainer>
                    <Controller
                        name={`${namePrefix}overnightAcknowledged`}
                        control={control}
                        render={(controllerProps) => (
                            <>
                                <FormControlLabel
                                    control={<Checkbox {...controllerProps.field} />}
                                    label={t('overnight-confirmation')}
                                />
                                {controllerProps?.fieldState?.error && (
                                    <Error>{controllerProps.fieldState.error.message}</Error>
                                )}
                            </>
                        )}
                    />
                </AcknowledgeOvernightContainer>
            );
        }

        return null;
    };

    const lmsAttendanceAvailable = lmsSyncSettings?.sync_enabled && lmsSyncSettings.enable_attendance;
    return (
        <AddSessionsContainer onClose={onClose}>
            <FormControl fullWidth>
                <ControlledInput
                    type="time"
                    name={`${namePrefix}startTime`}
                    label={t('start-time')}
                    control={control}
                />
            </FormControl>

            <Separator>{t('to')}</Separator>

            <FormControl fullWidth>
                <ControlledInput type="time" name={`${namePrefix}endTime`} label={t('end-time')} control={control} />
            </FormControl>

            <Spacer />

            <StyledAutocomplete fullWidth>
                <ControlledAutocomplete
                    autoHighlight
                    name={`${namePrefix}timezone`}
                    label={t('timezone')}
                    control={control}
                    options={timezoneOptions}
                />
            </StyledAutocomplete>

            <DateRangeContainer>
                <ControlledDateRange
                    name={`${namePrefix}dateRange`}
                    startLabel={t('start-date')}
                    endLabel={t('end-date')}
                    control={control}
                    separator={t('to')}
                    calendars={1}
                    defaultValue={[moment(), moment().add(6, 'days')]}
                />
            </DateRangeContainer>

            <Spacer />

            <FormControl fullWidth>
                <ControlledSelect
                    name={`${namePrefix}days`}
                    defaultValue={[]}
                    multiple
                    label={t('days-of-the-week')}
                    control={control}
                >
                    <MenuItem value={0}>
                        <span>{t('sunday')}</span>
                    </MenuItem>
                    <MenuItem value={1}>
                        <span>{t('monday')}</span>
                    </MenuItem>
                    <MenuItem value={2}>
                        <span>{t('tuesday')}</span>
                    </MenuItem>
                    <MenuItem value={3}>
                        <span>{t('wednesday')}</span>
                    </MenuItem>
                    <MenuItem value={4}>
                        <span>{t('thursday')}</span>
                    </MenuItem>
                    <MenuItem value={5}>
                        <span>{t('friday')}</span>
                    </MenuItem>
                    <MenuItem value={6}>
                        <span>{t('saturday')}</span>
                    </MenuItem>
                </ControlledSelect>
            </FormControl>

            <FormControl fullWidth style={{ gridColumnStart: 1, gridColumnEnd: 2 }}>
                <ControlledInput
                    type="text"
                    name={`${namePrefix}label`}
                    control={control}
                    label={<>{t('section-label')}</>}
                />
            </FormControl>

            {lmsAttendanceAvailable && (
                <FormControl fullWidth style={{ gridColumnStart: 3, gridColumnEnd: 4 }}>
                    <ControlledSelect
                        name={`${namePrefix}attendanceType`}
                        label={t('track-attendance-label')}
                        control={control}
                        value={attendanceType}
                    >
                        <MenuItem value={AttendanceType.Native}>
                            <span>{t(AttendanceType.Native)}</span>
                        </MenuItem>
                        <MenuItem value={AttendanceType.IntegrateWithLms}>
                            <span>{t(AttendanceType.IntegrateWithLms)}</span>
                        </MenuItem>
                    </ControlledSelect>
                </FormControl>
            )}

            {classGroups && classGroups.length > 0 && (
                <FormControl fullWidth style={{ gridColumnStart: 5, gridColumnEnd: 5 }}>
                    <ControlledSelect
                        name={`${namePrefix}selectedGroups`}
                        multiple
                        label={t('student-groups')}
                        control={control}
                    >
                        <MenuItem value={'all'}>
                            <span>{t('all-groups')}</span>
                        </MenuItem>
                        {classGroups?.map((classGroup) => (
                            <MenuItem value={classGroup.c_student_group_id} key={classGroup.c_student_group_id}>
                                <span>{classGroup.label}</span>
                            </MenuItem>
                        ))}
                    </ControlledSelect>
                </FormControl>
            )}
            <Divider style={{ gridColumnStart: 1, gridColumnEnd: 6 }}></Divider>
            {onAddMore && (
                <Button
                    style={{ gridColumnStart: 5, gridColumnEnd: 6, height: '40px', marginTop: '24px' }}
                    onClick={onAddMore}
                    variant="contained"
                >
                    {t('add-more-dates')}
                </Button>
            )}

            {renderOvernightAcknowledgement()}
        </AddSessionsContainer>
    );
};
